/**
 * Which organizations the Stats page reads, and what it calls them (ATH-247).
 *
 * Several organizations can be read as one, up to "All": every organization the caller can pick.
 * What "All" covers therefore depends on who is asking.
 */

interface ScopeOrg {
    id: string;
    name: string;
    parentOrganizationId?: string | null;
}

/** The organizations at the top of what the caller can see: those whose parent they cannot pick. */
export const rootOrgIds = (orgs: ScopeOrg[]): string[] => {
    const visible = new Set(orgs.map((org) => org.id));
    return orgs
        .filter((org) => !org.parentOrganizationId || !visible.has(org.parentOrganizationId))
        .map((org) => org.id);
};

/**
 * Whether to offer "All organizations" while choosing one organization at a time.
 *
 * Only when what the caller can see does not roll up to one top-level organization. With one,
 * picking it already covers its sub-orgs: joining a sub-org joins its parent too, and every action
 * is recorded under each organization the person belongs to.
 */
export const offersAll = (orgs: ScopeOrg[]): boolean => rootOrgIds(orgs).length > 1;

/** True when the selection is every organization the caller can pick. */
export const isAllSelected = (selectedIds: string[], orgs: ScopeOrg[]): boolean =>
    orgs.length > 1 && selectedIds.length === orgs.length;

/** What the page calls the selection: the organization's name, "All organizations", or "N organizations". */
export const scopeLabel = (selectedIds: string[], orgs: ScopeOrg[]): string | undefined => {
    if (selectedIds.length === 0) return undefined;
    if (isAllSelected(selectedIds, orgs)) return "All organizations";
    if (selectedIds.length === 1) return orgs.find((org) => org.id === selectedIds[0])?.name ?? "Organization";
    return `${selectedIds.length} organizations`;
};

/** An organization's name with its parent's in front, so a sub-org's export sheet says whose it is. */
export const orgLabel = (org: ScopeOrg, orgs: ScopeOrg[]): string => {
    const parent = orgs.find((candidate) => candidate.id === org.parentOrganizationId);
    return parent ? `${parent.name} - ${org.name}` : org.name;
};
